import { ConsentSettings, PrivacyConfig } from '../types/analytics';

/**
 * Consent Manager for privacy compliance and user consent handling
 * Manages user consent preferences and provides opt-out functionality
 */
export class ConsentManager {
  private consentSettings: ConsentSettings;
  private readonly config: PrivacyConfig;
  private readonly storageKey: string;
  private consentListeners: Array<(consent: ConsentSettings) => void> = [];

  constructor(config?: Partial<PrivacyConfig>) {
    // Default privacy configuration with strict defaults
    this.config = {
      defaultConsent: {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        functionality_storage: 'granted',
        personalization_storage: 'denied',
      },
      showConsentBanner: true,
      consentStorageKey: 'sailing_handicap_analytics_consent',
      dataRetentionMonths: 26,
      anonymizeIp: true,
      ...config,
    };

    this.storageKey = this.config.consentStorageKey;

    // Initialize consent settings from storage or defaults
    this.consentSettings = this.loadConsentFromStorage() || this.config.defaultConsent;
  }

  /**
     * Get current consent settings
     * @return {ConsentSettings} Current consent configuration
     */
  getConsent(): ConsentSettings {
    return { ...this.consentSettings };
  }

  /**
     * Update consent settings and persist to storage
     * @param {Partial<ConsentSettings>} newConsent - New consent settings
     */
  setConsent(newConsent: Partial<ConsentSettings>): void {
    try {
      // Validate consent settings
      const validatedConsent = this.validateConsentSettings(newConsent);

      // Update internal state
      this.consentSettings = {
        ...this.consentSettings,
        ...validatedConsent,
      };

      // Business rule: Always ensure functionality_storage is 'granted' for app operation
      this.consentSettings.functionality_storage = 'granted';

      // Persist to storage
      this.saveConsentToStorage(this.consentSettings);

      // Notify listeners
      this.notifyConsentListeners(this.consentSettings);

      console.log('ConsentManager: Consent settings updated', this.consentSettings);
    } catch (error) {
      console.error('ConsentManager: Error updating consent settings:', error);
    }
  }

  /**
     * Grant all analytics consent
     * @return {void}
     */
  grantAllConsent(): void {
    this.setConsent({
      analytics_storage: 'granted',
      ad_storage: 'granted',
      functionality_storage: 'granted',
      personalization_storage: 'granted',
    });
  }

  /**
     * Deny all analytics consent (opt-out)
     * @return {void}
     */
  denyAllConsent(): void {
    this.setConsent({
      analytics_storage: 'denied',
      ad_storage: 'denied',
      functionality_storage: 'granted', // Keep functionality for app operation
      personalization_storage: 'denied',
    });
  }

  /**
     * Grant only essential analytics consent
     * @return {void}
     */
  grantEssentialConsent(): void {
    this.setConsent({
      analytics_storage: 'granted',
      ad_storage: 'denied',
      functionality_storage: 'granted',
      personalization_storage: 'denied',
    });
  }

  /**
     * Check if analytics tracking is allowed
     * @return {boolean} True if analytics consent is granted
     */
  isAnalyticsAllowed(): boolean {
    return this.consentSettings.analytics_storage === 'granted';
  }

  /**
     * Check if personalization is allowed
     * @return {boolean} True if personalization consent is granted
     */
  isPersonalizationAllowed(): boolean {
    return this.consentSettings.personalization_storage === 'granted';
  }

  /**
     * Check if user has made a consent choice
     * @return {boolean} True if consent has been explicitly set by user
     */
  hasUserMadeChoice(): boolean {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored !== null;
    } catch (error) {
      console.warn('ConsentManager: Cannot check user choice - localStorage unavailable');
      return false;
    }
  }

  /**
     * Reset consent to default settings
     */
  resetConsent(): void {
    try {
      // Clear stored consent
      localStorage.removeItem(this.storageKey);

      // Reset to defaults
      this.consentSettings = { ...this.config.defaultConsent };

      // Notify listeners
      this.notifyConsentListeners(this.consentSettings);

      console.log('ConsentManager: Consent reset to defaults');
    } catch (error) {
      console.error('ConsentManager: Error resetting consent:', error);
    }
  }

  /**
     * Add listener for consent changes
     * @param {function(ConsentSettings): void} listener - Function to call when consent changes
     */
  addConsentListener(listener: (consent: ConsentSettings) => void): void {
    this.consentListeners.push(listener);
  }

  /**
     * Remove consent change listener
     * @param {function(ConsentSettings): void} listener - Function to remove from listeners
     */
  removeConsentListener(listener: (consent: ConsentSettings) => void): void {
    const index = this.consentListeners.indexOf(listener);
    if (index > -1) {
      this.consentListeners.splice(index, 1);
    }
  }

  /**
     * Get privacy configuration
     * @return {PrivacyConfig} Current privacy configuration
     */
  getPrivacyConfig(): PrivacyConfig {
    return { ...this.config };
  }

  /**
     * Check if consent banner should be shown
     * @return {boolean} True if banner should be displayed
     */
  shouldShowConsentBanner(): boolean {
    return this.config.showConsentBanner && !this.hasUserMadeChoice();
  }

  /**
     * Filter potentially sensitive data from parameters
     * @param {Record<string, any>} data - Data object to filter
     * @return {Record<string, string | number | boolean | Array<string | number | boolean>>} Filtered data with PII removed
     */
  filterPII(data: Record<string, unknown>): Record<string, string | number | boolean | Array<string | number | boolean>> {
    if (!data || typeof data !== 'object') {
      return {};
    }

    const filtered: Record<string, string | number | boolean | Array<string | number | boolean>> = {};
    const piiKeys = [
      'email', 'name', 'phone', 'address', 'ip', 'user_id',
      'username', 'password', 'ssn', 'credit_card', 'personal_id',
    ];

    for (const [key, value] of Object.entries(data)) {
      // Skip potential PII keys
      const keyLower = key.toLowerCase();
      const isPII = piiKeys.some((piiKey) => keyLower.includes(piiKey));

      if (isPII) {
        console.warn(`ConsentManager: Filtering potential PII key: ${key}`);
        continue;
      }

      // Handle null and undefined values
      if (value === null || value === undefined) {
        continue;
      }

      // Only allow safe data types
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        filtered[key] = value;
      } else if (Array.isArray(value)) {
        // Filter array elements
        const filteredArray = value.filter((item) =>
          item !== null && item !== undefined &&
          (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'),
        );
        if (filteredArray.length > 0) {
          filtered[key] = filteredArray;
        }
      }
    }

    return filtered;
  }

  /**
     * Validate consent settings format
     * @param {Partial<ConsentSettings>} consent - Consent settings to validate
     * @return {Partial<ConsentSettings>} Validated consent settings
     */
  private validateConsentSettings(consent: Partial<ConsentSettings>): Partial<ConsentSettings> {
    const validated: Partial<ConsentSettings> = {};
    const validValues: Array<'granted' | 'denied'> = ['granted', 'denied'];

    // Validate each consent type
    const consentTypes: Array<keyof ConsentSettings> = [
      'analytics_storage', 'ad_storage', 'functionality_storage', 'personalization_storage',
    ];

    for (const type of consentTypes) {
      if (consent[type] && validValues.includes(consent[type] as 'granted' | 'denied')) {
        validated[type] = consent[type];
      }
    }

    return validated;
  }

  /**
     * Load consent settings from localStorage
     * @return {ConsentSettings|null} Stored consent settings or null if not found
     */
  private loadConsentFromStorage(): ConsentSettings | null {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Validate stored data structure
        if (this.isValidConsentSettings(parsed)) {
          return parsed;
        } else {
          console.warn('ConsentManager: Invalid stored consent data, using defaults');
          localStorage.removeItem(this.storageKey);
        }
      }
    } catch (error) {
      console.warn('ConsentManager: Error loading consent from storage:', error);
    }

    return null;
  }

  /**
     * Save consent settings to localStorage
     * @param {ConsentSettings} consent - Consent settings to save
     */
  private saveConsentToStorage(consent: ConsentSettings): void {
    try {
      const serialized = JSON.stringify(consent);
      localStorage.setItem(this.storageKey, serialized);
    } catch (error) {
      console.error('ConsentManager: Error saving consent to storage:', error);
    }
  }

  /**
     * Validate consent settings structure
     * @param {any} data - Data to validate
     * @return {boolean} True if data is valid consent settings
     */
  private isValidConsentSettings(data: unknown): data is ConsentSettings {
    if (!data || typeof data !== 'object') {
      return false;
    }

    const requiredKeys: Array<keyof ConsentSettings> = [
      'analytics_storage', 'ad_storage', 'functionality_storage', 'personalization_storage',
    ];
    const validValues = ['granted', 'denied'];

    return requiredKeys.every((key) =>
      (data as Record<string, unknown>)[key] && validValues.includes((data as Record<string, unknown>)[key] as string),
    );
  }

  /**
     * Notify all consent listeners of changes
     * @param {ConsentSettings} consent - New consent settings
     */
  private notifyConsentListeners(consent: ConsentSettings): void {
    this.consentListeners.forEach((listener) => {
      try {
        listener(consent);
      } catch (error) {
        console.error('ConsentManager: Error notifying consent listener:', error);
      }
    });
  }
}

// Export singleton instance
export const consentManager = new ConsentManager();
export default consentManager;
